// src/components/CompareVehicles.tsx
import { Link, useSearchParams } from 'react-router';
import { X, CheckCircle, XCircle, ArrowLeft, Plus } from 'lucide-react';
import { useState, useEffect } from "react";
import { getVehiclesForComparison } from "../../../services/publicVehicle.service";
import { toast } from "sonner";

// =====================================================
// SKELETON (unchanged)
// =====================================================
const Skeleton = ({ className = "" }: { className?: string }) => (
  <div className={`animate-pulse bg-gray-200 rounded-lg ${className}`} />
);

function CompareVehiclesSkeleton() {
  return (
    <div className="min-h-screen bg-[#F5F7FA]">
      <div className="max-w-7xl mx-auto px-4 py-8">

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <Skeleton className="h-8 w-56 mb-2" />
            <Skeleton className="h-4 w-64" />
          </div>

          <Skeleton className="h-5 w-32" />
        </div>

        <div className="overflow-x-auto">
          <div className="min-w-[700px]">

            {/* Vehicle Cards */}
            <div
              className="grid gap-4 mb-6"
              style={{ gridTemplateColumns: "200px repeat(3, 1fr)" }}
            >
              <div className="flex items-end">
                <Skeleton className="h-5 w-16" />
              </div>

              {[1, 2, 3].map((item) => (
                <div
                  key={item}
                  className="bg-white rounded-2xl border border-[#E5E7EB] overflow-hidden"
                >
                  <Skeleton className="w-full h-36 rounded-none" />

                  <div className="p-3">
                    <Skeleton className="h-5 w-40 mx-auto mb-2" />
                    <Skeleton className="h-5 w-24 mx-auto mb-3" />
                    <Skeleton className="h-9 w-full rounded-lg" />
                  </div>
                </div>
              ))}
            </div>

            {/* Specifications */}
            <div className="bg-white rounded-2xl border border-[#E5E7EB] overflow-hidden mb-5">

              <div className="px-5 py-3 border-b border-[#E5E7EB]">
                <Skeleton className="h-6 w-36" />
              </div>

              {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((row) => (
                <div
                  key={row}
                  className="grid gap-4 px-5 py-3 border-b border-[#F3F4F6]"
                  style={{ gridTemplateColumns: "200px repeat(3, 1fr)" }}
                >
                  <Skeleton className="h-4 w-24" />

                  {[1, 2, 3].map((col) => (
                    <Skeleton
                      key={col}
                      className="h-4 w-24"
                    />
                  ))}
                </div>
              ))}

            </div>

            {/* Features */}
            <div className="bg-white rounded-2xl border border-[#E5E7EB] overflow-hidden">

              <div className="px-5 py-3 border-b border-[#E5E7EB]">
                <Skeleton className="h-6 w-24" />
              </div>

              {[1, 2, 3, 4, 5, 6, 7, 8].map((row) => (
                <div
                  key={row}
                  className="grid gap-4 px-5 py-3 border-b border-[#F3F4F6]"
                  style={{ gridTemplateColumns: "200px repeat(3, 1fr)" }}
                >
                  <Skeleton className="h-4 w-28" />

                  {[1, 2, 3].map((col) => (
                    <div
                      key={col}
                      className="flex justify-start"
                    >
                      <Skeleton className="w-5 h-5 rounded-full" />
                    </div>
                  ))}
                </div>
              ))}

            </div>

          </div>
        </div>

      </div>
    </div>
  );
}

// =====================================================
// COMPARE VEHICLES LABELS (unchanged)
// =====================================================
const SPEC_LABELS: Record<string, string> = {
  year: 'Year', mileage: 'Mileage', fuel: 'Fuel Type', transmission: 'Transmission',
  engine: 'Engine', power: 'Power', color: 'Color', seats: 'Seats', drive: 'Drive Type',
};

// const FEATURE_LABELS: Record<string, string> = {
//   leather: 'Leather Seats', panoramic: 'Panoramic Roof', navigation: 'Navigation',
//   heated: 'Heated Seats', camera360: '360° Camera', cruise: 'Cruise Control',
//   blindSpot: 'Blind Spot Monitor', airSusp: 'Air Suspension',
// };

// =====================================================
// MAIN COMPONENT
// =====================================================
export function CompareVehicles() {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [vehicles, setVehicles] = useState<any[]>([]);

  // Get vehicle IDs from URL query params (e.g., ?ids=uuid1,uuid2,uuid3)
  const idsParam = searchParams.get('ids');
  const vehicleIds = idsParam ? idsParam.split(',').filter(id => id.trim() !== '') : [];

  useEffect(() => {
  const fetchVehicles = async () => {
    if (!vehicleIds.length) {
      setLoading(false);
      setVehicles([]);
      return;
    }

    try {
      setLoading(true);

      const response = await getVehiclesForComparison(vehicleIds);

      if (response.success) {
        const orderedData = vehicleIds
          .map(id => response.data.find((v: any) => v.id === id))
          .filter(Boolean);

        setVehicles(orderedData);
      } else {
        toast.error(response.message || 'Failed to load comparison data');
      }
    } catch (error: any) {
      toast.error(error?.message || 'Failed to load vehicles');
    } finally {
      setLoading(false);
    }
  };

  fetchVehicles();
}, [idsParam]);

  // Helper to find the "best" vehicle index for a spec key
  const allFeatures = Array.from(
  new Map(
    vehicles.flatMap((vehicle: any) =>
      Array.isArray(vehicle.features)
        ? vehicle.features.map((feature: any) => [
            feature.id,
            feature,
          ])
        : []
    )
  ).values()
);
  const findBestIndex = (key: string) => {
    const values = vehicles.map((v: any) => {
      const val = v.specs[key];
      if (key === 'year') return parseInt(val) || 0;
      if (key === 'mileage') {
        const num = parseInt(String(val).replace(/,/g, ''));
        return isNaN(num) ? Infinity : num;
      }
      if (key === 'power') {
        const num = parseInt(String(val));
        return isNaN(num) ? 0 : num;
      }
      return 0;
    });

    if (key === 'year') {
      const max = Math.max(...values);
      return values.indexOf(max);
    }
    if (key === 'mileage') {
      const min = Math.min(...values);
      return values.indexOf(min);
    }
    if (key === 'power') {
      const max = Math.max(...values);
      return values.indexOf(max);
    }
    return -1;
  };

  // Loading state
  if (loading) {
    return <CompareVehiclesSkeleton />;
  }

  // No vehicles
  if (!vehicles.length) {
    return (
      <div className="min-h-screen bg-[#F5F7FA] flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl border border-[#E5E7EB] p-10 max-w-md w-full text-center shadow-xl">
          <h2 className="text-2xl text-[#2D323A] mb-2">No Vehicles to Compare</h2>
          <p className="text-[#717182] mb-6">Add at least one vehicle to start comparing.</p>
          <Link to="/search" className="inline-block px-6 py-3 bg-[#1E6DFF] text-white rounded-xl hover:bg-[#1557d9] transition-colors">
            Browse Vehicles
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F7FA]">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl text-[#2D323A]">Compare Vehicles</h1>
            <p className="text-[#717182]">Side-by-side comparison of {vehicles.length} vehicles</p>
          </div>
          <Link to="/search" className="flex items-center gap-2 text-[#1E6DFF] hover:underline text-sm">
            <ArrowLeft className="w-4 h-4" /> Back to Search
          </Link>
        </div>

        <div className="overflow-x-auto">
          <div className="min-w-[700px]">
            {/* Vehicle Headers */}
            <div className="grid gap-4 mb-6" style={{ gridTemplateColumns: `200px repeat(${Math.max(vehicles.length, 3)}, 1fr)` }}>
              <div className="flex items-end pb-2">
                <span className="text-sm text-[#717182]">Vehicle</span>
              </div>
              {vehicles.map((v: any) => (
                <div key={v.id} className="bg-white rounded-2xl border border-[#E5E7EB] overflow-hidden">
                  <div className="relative">
                    <img src={v.img || 'https://via.placeholder.com/400x260?text=No+Image'} alt={v.title} className="w-full h-36 object-cover" />
                    <Link
                      to={`/compare?ids=${vehicleIds.filter(id => id !== v.id).join(',')}`}
                      className="absolute top-2 right-2 p-1.5 bg-white/80 backdrop-blur rounded-lg text-[#717182] hover:text-[#EF4444]"
                    >
                      <X className="w-4 h-4" />
                    </Link>
                  </div>
                  <div className="p-3 text-center">
                    <p className="text-sm text-[#2D323A] leading-tight mb-1">{v.title}</p>
                    <p className="text-[#1E6DFF]">{Number(v.price).toLocaleString()} MAD</p>
                    <Link to={`/vehicle/${v.id}`} className="mt-2 block w-full py-1.5 bg-[#1E6DFF] text-white rounded-lg text-xs hover:bg-[#1557d9] transition-colors">
                      View Details
                    </Link>
                  </div>
                </div>
              ))}
              {/* Add Vehicle placeholder (always shown, but only clickable if < 3 vehicles) */}
              {vehicles.length < 3 && (
                <Link
                  to="/search?compare=true"
                  className="bg-white rounded-2xl border-2 border-dashed border-[#E5E7EB] flex flex-col items-center justify-center p-6 text-[#717182] hover:border-[#1E6DFF] hover:text-[#1E6DFF] transition-colors h-48"
                >
                  <Plus className="w-8 h-8 mb-2" />
                  <span className="text-sm">Add Vehicle</span>
                </Link>
              )}
            </div>

            {/* Specifications */}
            <div className="bg-white rounded-2xl border border-[#E5E7EB] overflow-hidden mb-4">
              <div className="px-5 py-3 bg-[#F5F7FA] border-b border-[#E5E7EB]">
                <h3 className="text-[#2D323A]">Specifications</h3>
              </div>
              {Object.keys(SPEC_LABELS).map((key, rowIdx) => {
                const bestIdx = findBestIndex(key);
                return (
                  <div
                    key={key}
                    className={`grid gap-4 px-5 py-3 ${rowIdx % 2 === 0 ? '' : 'bg-[#F5F7FA]/50'}`}
                    style={{ gridTemplateColumns: `200px repeat(${Math.max(vehicles.length, 1)}, 1fr)` }}
                  >
                    <span className="text-sm text-[#717182]">{SPEC_LABELS[key]}</span>
                    {vehicles.map((v: any, i: number) => {
                      const isBest = bestIdx === i && bestIdx !== -1;
                      const value = v.specs[key] || '—';
                      return (
                        <span key={v.id} className={`text-sm ${isBest ? 'text-[#22C55E]' : 'text-[#2D323A]'}`}>
                          {value}
                          {isBest && <span className="ml-1 text-xs bg-[#22C55E]/10 text-[#22C55E] px-1 py-0.5 rounded">Best</span>}
                        </span>
                      );
                    })}
                  </div>
                );
              })}
            </div>

            {/* Features */}
            <div className="bg-white rounded-2xl border border-[#E5E7EB] overflow-hidden">
              <div className="px-5 py-3 bg-[#F5F7FA] border-b border-[#E5E7EB]">
                <h3 className="text-[#2D323A]">Features</h3>
              </div>
              {allFeatures.map((feature: any, rowIdx) => (
  <div
    key={feature.id}
    className={`grid gap-4 px-5 py-3 ${
      rowIdx % 2 === 0 ? '' : 'bg-[#F5F7FA]/50'
    }`}
    style={{
      gridTemplateColumns: `200px repeat(${Math.max(
        vehicles.length,
        1
      )}, 1fr)`,
    }}
  >
    {/* Feature Name */}
    <span className="text-sm text-[#717182]">
      {feature.name}
    </span>

    {/* Vehicle-wise Feature Availability */}
    {vehicles.map((vehicle: any) => {
      const hasFeature = Array.isArray(vehicle.features)
        ? vehicle.features.some(
            (item: any) => item.id === feature.id
          )
        : false;

      return (
        <div
          key={vehicle.id}
          className="flex justify-start"
        >
          {hasFeature ? (
            <CheckCircle className="w-5 h-5 text-[#22C55E]" />
          ) : (
            <XCircle className="w-5 h-5 text-[#BFC2C7]" />
          )}
        </div>
      );
    })}
  </div>
))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
